import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Notifications } from '@mantine/notifications'
import { ThemeProvider } from './theme'
import { AuthProvider, msalInstance, isEntraConfigured } from './auth'
import App from './App'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
// EB Garamond (display face). Self-hosted via @fontsource — no third-party
// request at runtime. All subsets ship, but `unicode-range` means a browser only
// downloads the ~43 KB latin subset it actually needs.
// The shared token layer only NAMES the family; loading it is the app's job.
import '@fontsource-variable/eb-garamond'
import '@fontsource-variable/inter'
// Token layer first, then primitives, so app styles can override both rather
// than fight cascade order.
import '@lighthouse/design/tokens.css'
import '@lighthouse/design/primitives.css'
import './styles.css'
import './shell.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

async function startApp() {
  if (isEntraConfigured() && msalInstance) {
    await msalInstance.initialize()

    // Process the auth code returned by Entra ID after redirect
    const response = await msalInstance.handleRedirectPromise()
    if (response) {
      msalInstance.setActiveAccount(response.account)
    }

    // Restore active account from a previous session
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0])
    }
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            {/*
              Mantine renders notifications only where this is mounted. Its stylesheet was imported
              above but the component never was, so every notifications.show() in the app -- the
              data-entry admin screens included -- resolved to nothing at all: no toast, no error, no
              clue that a save had failed.
            */}
            <Notifications position="top-right" />
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ThemeProvider>
        </QueryClientProvider>
      </AuthProvider>
    </StrictMode>,
  )
}

startApp()
